import request from '@/utils/request'

/**
 * 财务管理 - 发票申请审核
 */
const apiPath = '/cust/customerOrder'

// Preserve the business reply so an explicit rejection cannot be confused with
// an unknown write result. Keep this local to the new attachment endpoints.
function attachmentRequest(path, data, options = {}) {
  return request.post(apiPath + path, data, { ...options, skipInterceptor: true }).then(response => response.data)
}

const invoiceApplicationAuditApi = {
  /**
   * 分页查询【发票申请审核】列表
   * @param {Object} data - 查询参数
   * @param {number} data.pageNo - 页码
   * @param {number} data.pageSize - 每页条数
   * @param {string} data.namePhone - 姓名电话
   * @param {number} data.levelId - 等级ID
   * @param {number} data.type - 发票类型 1电子发票
   * @param {number} data.titleType - 发票抬头类型 1个人 2公司
   * @param {number} data.state - 0用户提交开票申请 1后台审核通过 2用友系统开票完成 3用户取消开票申请 4开票失败
   * @param {number} data.timeType - 申请时间1 开票时间2
   * @param {string} data.startTime - 开始时间
   * @param {string} data.endTime - 结束时间
   * @param {string} data.invoiceId - 发票申请id，如果查详情值传这个字段
   */
  fetchList(data) {
    return request.post(apiPath + '/customerOrderInvoiceList', data)
  },

  /**
   * 获取发票申请详情
   * @param {string|number} id - 申请ID
   */
  fetchDetail(id) {
    return request.get(apiPath + '/detail/' + id)
  },

  /**
   * 获取发票文件地址（预览/下载共用）
   * 服务端只做数据权限校验并返回税务平台地址，文件由浏览器直连获取，
   * 不要在前端直接 fetch 税局地址——跨域必被拦截。
   * @param {Object} data - { invoiceId: 发票申请id, mode: 1预览 2下载（只影响建议文件名） }
   * 请求格式: application/x-www-form-urlencoded
   * 返回 data: { invoiceId, ordernum, title, state, mode, fileUrl, fileName, fileType, directAccess, tip }
   */
  customerOrderInvoiceFile(data) {
    const params = {
      invoiceId: data.invoiceId
    }
    if (data.mode != null) {
      params.mode = data.mode
    }
    return request.post(apiPath + '/customerOrderInvoiceFile', params, { isFormData: 1 })
  },

  attachments(invoiceId) {
    return attachmentRequest('/customerOrderInvoiceAttachments', { invoiceId }, { isFormData: 1 })
  },
  attachmentFile(data) {
    return attachmentRequest('/customerOrderInvoiceAttachmentFile', data, { isFormData: 1 })
  },
  uploadAttachment(data) {
    return attachmentRequest('/customerOrderInvoiceUpload', data, { timeout: 120000 })
  },
  completeManual(data) {
    return attachmentRequest('/customerOrderInvoiceCompleteManual', data)
  },
  deleteStaged(data) {
    return attachmentRequest('/customerOrderInvoiceDeleteStaged', data)
  },

  /**
   * 审核发票（修改发票状态）
   * @param {Object} data - { invoiceId: 发票申请id, state: 状态 1审核通过 4开票失败, cancelReason?: 取消/失败原因 }
   * 请求格式: application/x-www-form-urlencoded
   */
  invoiceState(data) {
    const params = {
      invoiceId: data.invoiceId,
      state: data.state
    }
    if (data.cancelReason != null && data.cancelReason !== '') {
      params.cancelReason = data.cancelReason
    }
    return request.post(apiPath + '/invoiceState', params, { isFormData: 1 })
  },

  /**
   * 同意申请（state=1 后台审核通过）
   * @param {Object} data - { invoiceId: 发票申请ID }
   */
  approve(data) {
    const id = data.invoiceId != null ? data.invoiceId : data.id
    return this.invoiceState({ invoiceId: id, state: data.state, cancelReason: data.cancelReason })
  },

  /**
   * 驳回申请（state=3 3用户取消开票申请，需传 cancelReason）
   * @param {Object} data - { invoiceId: 发票申请ID, reason: 驳回/失败原因 }
   */
  reject(data) {
    const id = data.invoiceId != null ? data.invoiceId : data.id
    return this.invoiceState({ invoiceId: id, state: 3, cancelReason: data.reason })
  },

  /**
   * 开票申请列表导出（建异步任务，产物在导出中心下载）。
   *
   * ⚠️ 路径就是后端方法名 downloadCustomerOrderInvoiceList，别改成好听的名字：
   * 后端同名字符串还会写进 sys_task.code，导出中心按它挂表单、按它筛任务（BUG-2026-581958）。
   * 这里原来是 createExportTask，指向 admin-api 里**不存在**的路径，且零调用。
   */
  downloadCustomerOrderInvoiceList(data) {
    return request.post(apiPath + '/downloadCustomerOrderInvoiceList', data)
  },

  /**
   * 核验申请人是否具备开具增值税专用发票的资质（REQ-2026-639960）
   * @param {Object} data - { invoiceId?: 开票申请id, customerId?: 客户id, userId?: 客户登录账号id }
   *                        三者按 invoiceId > customerId > userId 取第一个非空的用
   */
  checkSpecialQualification(data) {
    return request.get(apiPath + '/specialInvoiceQualification', { params: data })
  },

  /**
   * 标记客户纳税人类型（REQ-2026-639960）
   * @param {Object} data - { customerId: 客户id, taxpayerType: 1一般纳税人 2小规模纳税人 }
   */
  updateTaxpayerType(data) {
    return request.post(apiPath + '/updateTaxpayerType', {
      customerId: data.customerId,
      taxpayerType: data.taxpayerType
    }, { isFormData: 1 })
  }
}

export default invoiceApplicationAuditApi
